import { useMemo } from 'react';
import { useFloorPlansForSelectFromWaitlistQuery } from './hooks.queries';

const getFloorPlanProgramNames = (floorPlan) =>
  (floorPlan?.floorPlanAffordablePrograms ?? [])
    .map((fp) => fp?.propertyAffordableProgram?.masterAffordableProgram?.name)
    .filter(Boolean);

const useSelectFromWaitlistModal = ({
  organizationId,
  propertyId,
  bedroomCount,
  selectedFloorPlanId,
  separateByBedroom,
  waitlistV2Programs,
}) => {
  const { data: floorPlans = [], isLoading: isLoadingFloorPlans } =
    useFloorPlansForSelectFromWaitlistQuery(organizationId, propertyId);

  const eligibleFloorPlans = useMemo(
    () =>
      floorPlans.filter((floorPlan) => {
        const matchesBedroom =
          !separateByBedroom ||
          bedroomCount === null ||
          floorPlan?.nBeds === bedroomCount;
        const floorPlanPrograms = getFloorPlanProgramNames(floorPlan);
        const matchesProgram = floorPlanPrograms.some((name) =>
          (waitlistV2Programs ?? []).includes(name),
        );
        return matchesBedroom && matchesProgram;
      }),
    [floorPlans, separateByBedroom, bedroomCount, waitlistV2Programs],
  );

  const floorPlanOptions = useMemo(
    () =>
      eligibleFloorPlans.map((floorPlan) => ({
        value: floorPlan.id,
        text: `${floorPlan.internalName} "${floorPlan.marketingName}"`,
      })),
    [eligibleFloorPlans],
  );

  const fundingProgramsDisplay = useMemo(
    () => (waitlistV2Programs ?? []).join(', '),
    [waitlistV2Programs],
  );

  const floorPlanProgramsDisplay = useMemo(() => {
    if (!selectedFloorPlanId) return '---';
    const floorPlan = eligibleFloorPlans.find(
      (fp) => fp.id === selectedFloorPlanId,
    );
    if (!floorPlan) return '---';
    const names = getFloorPlanProgramNames(floorPlan);
    return names.length ? names.join(', ') : '---';
  }, [eligibleFloorPlans, selectedFloorPlanId]);

  const todayDateUTC = new Date().toISOString().slice(0, 10);

  return {
    isLoadingFloorPlans,
    floorPlanOptions,
    fundingProgramsDisplay,
    floorPlanProgramsDisplay,
    showBedroomsField: Boolean(separateByBedroom),
    todayDateUTC,
  };
};

export default useSelectFromWaitlistModal;
